import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { call } from '@/api/desk';
import { DataTable, Filters, FormModal, Link2, Modal, Notice, PageHeader, Search, Sel, Tabs, arr, dt, errMsg, useAction, useGet, useList, Pager } from '@/features/shared/ops/kit';
import { badge } from '../lib';
import { useAuth } from '@/stores/auth';

const ROLES = ['desk', 'teacher', 'bursar'].map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }));
const EMP = ['full_time', 'part_time', 'contract'].map((v) => ({ value: v, label: v.replace('_', ' ') }));

function StaffList() {
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const list = useList('desk-staff', '/school/staff', { q, role, status });
  const isOwner = useAuth((st) => st.user?.role) === 'owner';
  const roles = useGet<any>('desk-roles-pick', '/school/roles', undefined, isOwner);
  const roleOpts = arr(roles.data?.roles).map((r: any) => ({ value: r.id, label: r.name }));
  const a = useAction(() => list.refetch?.());
  const [form, setForm] = useState<null | { row?: any }>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const row = form?.row;

  const fields = [
    { name: 'first_name', label: 'First name', type: 'text' as const, required: true, maxLength: 60 },
    { name: 'last_name', label: 'Last name', type: 'text' as const, required: true, maxLength: 60 },
    { name: 'role', label: 'Role', type: 'select' as const, options: ROLES, required: true },
    { name: 'phone', label: 'Phone', type: 'text' as const, maxLength: 20 },
    { name: 'email', label: 'Email', type: 'email' as const, maxLength: 120 },
    { name: 'designation', label: 'Designation', type: 'text' as const, maxLength: 80 },
    { name: 'department', label: 'Department', type: 'text' as const, maxLength: 80 },
    { name: 'employment_type', label: 'Employment', type: 'select' as const, options: EMP },
    { name: 'hired_on', label: 'Hired on', type: 'date' as const },
    { name: 'qualification', label: 'Qualification', type: 'text' as const, maxLength: 120 },
    ...(row ? [{ name: 'status', label: 'Status', type: 'select' as const, options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }] }] : []),
  ];

  const openEdit = (r: any) => a.run(async () => {
    const d = await call('get', `/school/staff/${r.id}`);
    setForm({ row: { ...d.data, ...(d.data?.profile ?? {}) } });
    return d;
  });

  return (
    <Card title="Staff" action={<Button onClick={() => setForm({})}>Add staff</Button>}>
      <Filters>
        <Search value={q} onChange={setQ} placeholder="Search name, phone, email" />
        <Sel value={role} onChange={setRole} options={ROLES} placeholder="All roles" />
        <Sel value={status} onChange={setStatus} options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} placeholder="Any status" />
      </Filters>
      {a.banner}
      <DataTable loading={list.isLoading} error={list.error} rows={list.rows} empty="No staff found." cols={[
        { head: 'Name', cell: (r) => r.name },
        { head: 'Role', cell: (r) => r.role },
        { head: 'Contact', cell: (r) => r.phone || r.email || '—' },
        { head: 'Designation', cell: (r) => r.designation ?? '—' },
        { head: 'Last login', cell: (r) => (r.last_login_at ? dt(r.last_login_at) : 'Never') },
        { head: 'Status', cell: (r) => badge(r.status) },
        {
          head: 'Actions', cell: (r) => (
            <span className="flex gap-3">
              <Link2 onClick={() => openEdit(r)}>Edit</Link2>
              <Link2 onClick={() => a.run(async () => { const x = await call('post', `/school/staff/${r.id}/reset-password`); setSecret(x.data?.temp_password ?? ''); return x; }, { confirm: 'Reset this password?' })}>Reset password</Link2>
              {r.status === 'active' && <button type="button" className="text-red-600 text-sm hover:underline" onClick={() => a.run(() => call('post', `/school/staff/${r.id}/deactivate`), { confirm: 'Deactivate and sign this person out?', ok: 'Staff deactivated.' })}>Deactivate</button>}
            </span>
          ),
        },
      ]} />
      <Pager meta={list.meta} page={list.page} onPage={list.setPage} />
      {secret !== null && (
        <Modal title="Temporary password" onClose={() => setSecret(null)}>
          <p className="text-sm text-slate-600 mb-2">Share this once. It will not be shown again.</p>
          <code className="block rounded-xl bg-slate-100 p-3 text-lg select-all">{secret}</code>
        </Modal>
      )}
      {form && (
        <FormModal wide title={row ? 'Edit staff' : 'Add staff'} fields={row ? fields : [...fields, ...(roleOpts.length ? [] : [])]} initial={row ?? { role: 'teacher', employment_type: 'full_time' }} onClose={() => setForm(null)}
          onSubmit={async (v) => {
            const body: Record<string, unknown> = Object.fromEntries(Object.entries(v).filter(([k]) => !['id', 'name', 'profile', 'assignments', 'custom_roles', 'last_login_at', 'avatar_path', 'staff_no'].includes(k)).map(([k, x]) => [k, x === '' ? null : x]));
            const r = await call(row ? 'put' : 'post', row ? `/school/staff/${row.id}` : '/school/staff', { body });
            if (!row && r.data?.temp_password) setSecret(r.data.temp_password);
            list.refetch?.();
          }} />
      )}
    </Card>
  );
}

export function RolesPanel() {
  const q = useGet<any>('desk-roles', '/school/roles');
  const a = useAction(() => q.refetch());
  const [form, setForm] = useState<null | { row?: any }>(null);
  const [sel, setSel] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [err, setErr] = useState('');
  const perms = arr(q.data?.available_permissions);
  const modules = Array.from(new Set(perms.map((p: any) => p.module)));

  const open = (row?: any) => {
    setErr(''); setName(row?.name ?? ''); setDesc(row?.description ?? '');
    setSel(arr(row?.permission_keys ?? row?.permissions).map((p: any) => (typeof p === 'string' ? p : p.key)));
    setForm({ row });
  };
  const save = async () => {
    setErr('');
    try {
      await call(form?.row ? 'put' : 'post', form?.row ? `/school/roles/${form.row.id}` : '/school/roles', { body: { name, description: desc || null, permission_keys: sel } });
      setForm(null); q.refetch();
    } catch (e) { setErr(errMsg(e)); }
  };

  return (
    <Card title="Custom roles" action={<Button onClick={() => open()}>Add role</Button>}>
      {a.banner}
      <DataTable loading={q.isLoading} error={q.error} rows={arr(q.data?.roles)} empty="No custom roles." cols={[
        { head: 'Name', cell: (r) => r.name },
        { head: 'Description', cell: (r) => r.description ?? '' },
        { head: 'Permissions', cell: (r) => arr(r.permission_keys ?? r.permissions).length || '—' },
        { head: 'Members', cell: (r) => r.members ?? 0 },
        {
          head: 'Actions', cell: (r) => r.is_system ? <span className="text-xs text-slate-500">System role</span> : (
            <span className="flex gap-3">
              <Link2 onClick={() => open(r)}>Edit</Link2>
              <button type="button" className="text-red-600 text-sm hover:underline" onClick={() => a.run(() => call('delete', `/school/roles/${r.id}`), { confirm: 'Delete this role?', ok: 'Role deleted.' })}>Delete</button>
            </span>
          ),
        },
      ]} />
      {form && (
        <Modal wide title={form.row ? 'Edit role' : 'Add role'} onClose={() => setForm(null)}>
          {err && <Notice kind="error">{err}</Notice>}
          <div className="space-y-3">
            <input className="border rounded-xl px-3 py-2 text-sm w-full" placeholder="Role name" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
            <input className="border rounded-xl px-3 py-2 text-sm w-full" placeholder="Description (optional)" maxLength={200} value={desc} onChange={(e) => setDesc(e.target.value)} />
            <div className="max-h-72 overflow-auto space-y-3">
              {modules.map((m) => (
                <div key={String(m)}>
                  <p className="text-xs font-semibold uppercase text-slate-500 mb-1">{String(m)}</p>
                  <div className="grid grid-cols-2 gap-1">
                    {perms.filter((p: any) => p.module === m).map((p: any) => (
                      <label key={p.key} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sel.includes(p.key)} onChange={(e) => setSel(e.target.checked ? [...sel, p.key] : sel.filter((k) => k !== p.key))} />{p.label}</label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button><Button disabled={!name.trim() || sel.length === 0} onClick={save}>Save role</Button></div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

export function StaffPage() {
  const [tab, setTab] = useState('staff');
  // Custom roles are owner-only on the backend (school/roles allow(['owner'])).
  const owner = useAuth((st) => st.user?.role) === 'owner';
  return (
    <div>
      <PageHeader title="Staff" sub="Teachers, desk officers and bursars" />
      {owner && <Tabs value={tab} onChange={setTab} tabs={[{ id: 'staff', label: 'Staff' }, { id: 'roles', label: 'Roles' }]} />}
      {tab === 'staff' || !owner ? <StaffList /> : <RolesPanel />}
    </div>
  );
}
